import {
  PaymentMethodConditions,
  paymentMethodConditionsFromPaymentMethod,
  SimulatorLoanConditions,
} from '@nemuru-eng/checkout-js';
import {
  Currency,
  LenderMap,
  LenderName,
  Loan,
  PaymentMethod,
  PaymentMethodType,
  Pricing,
  ProcessingLenderStrategyType,
  StopCondition,
} from '@nemuru-eng/js-api-client';
import { ChooseStrategyType } from '@nemuru-eng/js-api-client/slim';

import { AutomobileItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/entities/AutomobileItem';
import { CartEntity } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/entities/CartEntity';
import { LoanConditionsGroup } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/ConditionsGroup/LoanConditionsGroup';
import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { LoanConditions } from '@/components/modules/Application/components/PaymentMethod/_shared/types/LoanConditions';
import { PaymentMethodRequestType } from '@/components/modules/Application/components/PaymentMethod/_shared/types/PaymentMethodRequestType';
import { AutoLoanConditionsGroup } from '@/components/modules/Application/components/PaymentMethod/AutoLoan/entities/ConditionsGroup/AutoLoanConditionsGroup';
import { LenderOptionDetail } from '@/components/modules/Application/components/PaymentMethod/AutoLoan/types/LenderOptionDetail';

export type AutoPaymentMethod = Extract<PaymentMethod, { type: PaymentMethodType.AUTO_LOAN }>;
type AutoLoanPaymentMethodRequest = PaymentMethodRequestType<PaymentMethodType.AUTO_LOAN>;

export class AutoLoanPaymentMethod extends AbstractLoanPaymentMethod {
  protected ALLOWED_PAYMENT_METHOD_TYPES = [PaymentMethodType.AUTO_LOAN];
  public selectedConditionsGroup: AutoLoanConditionsGroup | null = null;

  public readonly paymentMethod: AutoPaymentMethod | null = null;

  protected setAvailableFilters(): void {
    throw new Error('Method not implemented.');
  }

  protected getAvailableConditions(): LoanConditions[] {
    throw new Error('Method not implemented.');
  }

  protected getSelectedTermCandidate(): number {
    if (!this.selectedTerm) {
      throw new Error('Method not implemented.');
    }
    return this.selectedTerm;
  }

  protected getConditionsGroups(): LoanConditionsGroup[] {
    return [this.getSelectedConditionsGroupCandidate()];
  }

  protected getSelectedConditionsGroupCandidate(): LoanConditionsGroup {
    if (!this.selectedConditionsGroup) {
      throw new Error('Selected conditions group is not set');
    }
    return this.selectedConditionsGroup;
  }

  public areOptionsConfigurable(): boolean {
    return true;
  }
  public get proposedTerms(): number[] {
    throw new Error('Method not implemented.');
  }

  public setup(amount: number, currency: Currency): void {
    this.amount = amount;
    this.currency = currency;
    this.isSetup = true;
  }

  public setSelectedConditionsGroup(conditionsGroup: AutoLoanConditionsGroup | null): this {
    this.selectedConditionsGroup = conditionsGroup;
    return this;
  }

  public selectSimulatorConditions(simulatorConditions: SimulatorLoanConditions[] | null): this {
    const conditionsGroup = simulatorConditions ? new AutoLoanConditionsGroup(simulatorConditions) : null;
    return this.setSelectedConditionsGroup(conditionsGroup);
  }

  public getActiveProviderNames(): LenderName[] {
    return this.selectedConditionsGroup ? this.selectedConditionsGroup.providerNames() : [];
  }

  public getAllProviderNames() {
    if (!this.paymentMethodConditions) return [];
    const lenderNamesInPaymentMethodConditions = this.paymentMethodConditions.reduce(
      (acc: LenderName[], condition: PaymentMethodConditions) => {
        if (!condition.conditions) return acc;
        if (!condition.conditions.lenderName) return acc;
        if (acc.includes(condition.conditions.lenderName)) return acc;
        return [...acc, condition.conditions.lenderName];
      },
      [],
    );
    return lenderNamesInPaymentMethodConditions;
  }

  public updateSelectedTerm(term: number): this {
    this.selectedTerm = term;
    return this;
  }

  public setConditionsFromPaymentMethodOptions(paymentMethodOptions: AutoPaymentMethod, loan: Loan | undefined) {
    const options = paymentMethodOptions.options;

    let term = options?.period;

    if (!term && loan) {
      term = loan.loanConditions.loanCalculator.term.period ?? undefined;
    }

    if (!term) {
      //TODO => if no period should set max period
      console.error('Missing period in payment method configuration');
      return;
    }

    // Term
    this.updateSelectedTerm(term);

    // Amount
    const amount = this.amount;
    if (amount === null) {
      console.error('Payment method amount is not set');
      return;
    }

    const loanAnnualInterest = loan?.loanConditions.annualInterest;

    // Pin the term computed above (session period, else loan term) so the candidate matches selectedTerm
    const conditions = paymentMethodConditionsFromPaymentMethod(
      options ? { ...paymentMethodOptions, options: { ...options, period: term } } : paymentMethodOptions,
      {
        amount,
        terms: [term],
        ...(loanAnnualInterest && { annualInterests: [loanAnnualInterest] }),
      },
    );

    if (!conditions) return;

    this.paymentMethodConditions = conditions;

    // Find the right candidate conditions based on the processing strategy
    let candidateConditions = conditions.reduce<(typeof conditions)[number] | undefined>(
      (lowest, c) =>
        !lowest ||
        (c.conditions &&
          c.conditions.instalmentTotalAmount.value < (lowest.conditions?.instalmentTotalAmount.value ?? Infinity))
          ? c
          : lowest,
      undefined,
    );

    const manualOptions =
      options?.processingStrategy?.details?.orderBy === ChooseStrategyType.MANUAL
        ? options?.processingStrategy?.details?.options
        : undefined;

    if (manualOptions && manualOptions.length > 0) {
      const loanApplicationSelected = loan?.loanApplicationSelected;

      if (loanApplicationSelected) {
        const lenderName = loanApplicationSelected.lenderName;
        const annualInterest = loan.loanConditions.annualInterest;
        const insuranceType = loan.pricingInsurance?.type;
        const insuranceProvider = loan.pricingInsurance?.provider;

        const manualConditions = conditions.find(
          (c) =>
            c.conditions?.lenderName === lenderName &&
            (!insuranceType || c.conditions?.insurance?.type === insuranceType) &&
            (!insuranceProvider || c.conditions?.insurance?.provider === insuranceProvider) &&
            (!annualInterest || c.conditions?.annualInterest?.value === annualInterest),
        );
        if (!manualConditions) {
          console.error(`No conditions found for selected lender ${lenderName} with annual interest ${annualInterest}`);
          return;
        }
        candidateConditions = manualConditions;
      } else {
        const lenderName = manualOptions[0].lenderName;
        const annualInterest = manualOptions[0].annualInterest;
        const insuranceType = manualOptions[0].insuranceType;

        const manualConditions = conditions.find(
          (c) =>
            c.conditions?.lenderName === lenderName &&
            (!insuranceType || c.conditions?.insurance?.type === insuranceType) &&
            (!annualInterest || c.conditions?.annualInterest?.value === annualInterest),
        );
        if (!manualConditions) {
          console.error(`No conditions found for manual lender ${lenderName} with annual interest ${annualInterest}`);
          return;
        }
        candidateConditions = manualConditions;
      }
    }

    if (!candidateConditions || !candidateConditions.conditions) return;

    const bestCandidateConditions: SimulatorLoanConditions = {
      ...candidateConditions.conditions!,
      principal: candidateConditions.amount,
      currency: this.currency,
      type: this.paymentMethodType,
    };

    this.setSelectedConditionsGroup(new AutoLoanConditionsGroup([bestCandidateConditions]));
  }

  public toDto(agentPricings: Pricing[], cart: CartEntity): AutoLoanPaymentMethodRequest {
    if (!this.selectedConditionsGroup?.getCandidateConditions()) {
      throw new Error('Term is not selected');
    }

    const registrationDate = cart.items.find((item) => item instanceof AutomobileItem)?.registrationDate;

    const processingStrategyOptions = this.getProcessingStrategyDetails(
      this.selectedConditionsGroup.simulatorConditions,
      agentPricings,
    );

    const uniqueLenderOptions = processingStrategyOptions.reduce((acc, option) => {
      if (!option) return acc;
      if (acc.some((item) => item.lenderName === option.lenderName)) return acc;
      acc.push(option);
      return acc;
    }, [] as LenderOptionDetail[]);

    const lenderOptions = uniqueLenderOptions.map((option) =>
      this.keepOnlyFlagOverrides(option, this.selectedConditionsGroup as AutoLoanConditionsGroup),
    );

    return {
      type: this.paymentMethodType as AutoLoanPaymentMethodRequest['type'],
      options: {
        period: this.selectedConditionsGroup?.getCandidateConditions().term,
        insuranceType: this.selectedConditionsGroup?.getCandidateConditions().insurance?.type,
        hasSetupFee: (this.selectedConditionsGroup as AutoLoanConditionsGroup).hasSetupFee,
        hasMerchantDiscountPercentage: (this.selectedConditionsGroup as AutoLoanConditionsGroup)
          .hasMerchantDiscountPercentage,
        hasGracePeriod: (this.selectedConditionsGroup as AutoLoanConditionsGroup).hasGracePeriod,
        registrationDate,
        processingStrategy: {
          type: ProcessingLenderStrategyType.WATERFALL,
          details: {
            orderBy: ChooseStrategyType.MANUAL,
            options: lenderOptions,
            stopCondition: StopCondition.ERROR,
          },
        },
      },
    };
  }

  private keepOnlyFlagOverrides(option: LenderOptionDetail, overall: AutoLoanConditionsGroup): LenderOptionDetail {
    const { hasSetupFee, hasGracePeriod, ...optionWithoutFlags } = option;

    return {
      ...optionWithoutFlags,
      ...(hasSetupFee !== overall.hasSetupFee && { hasSetupFee }),
      ...(hasGracePeriod !== overall.hasGracePeriod && { hasGracePeriod }),
    };
  }

  private getProcessingStrategyDetails(simulatorConditions: SimulatorLoanConditions[], agentPricings: Pricing[]) {
    if (!simulatorConditions) return [];

    return simulatorConditions
      .map((simulatorCondition) => {
        if (!simulatorCondition.lenderName || !simulatorCondition.pricingName) {
          return null;
        }

        const pricing = agentPricings.find((pricing) => pricing.id === simulatorCondition.pricingId);

        if (!pricing) return null;

        const range = pricing.ranges.find(
          (range) =>
            range.rangePeriodMin &&
            range.rangePeriodMax &&
            simulatorCondition.term &&
            range.rangePeriodMin <= simulatorCondition.term &&
            range.rangePeriodMax >= simulatorCondition.term &&
            range.rangePrincipalMin < simulatorCondition.principal.value &&
            range.rangePrincipalMax >= simulatorCondition.principal.value,
        );

        if (!range) return null;

        const fee = range.lenderEarningsFees?.find(
          (fee) => LenderMap.fromLenderId(fee.lenderId) === simulatorCondition.lenderName,
        );

        if (!fee || !fee.pricingNameInLenderContext) return null;

        const { hasSetupFee, hasGracePeriod } = new AutoLoanConditionsGroup([simulatorCondition]);

        return {
          lenderName: simulatorCondition.lenderName,
          pricingName: fee.pricingNameInLenderContext,
          insuranceType: simulatorCondition.insurance?.type,
          annualInterest: simulatorCondition.annualInterest.value,
          hasSetupFee,
          hasGracePeriod,
        };
      })
      .filter((simulatorCondition) => simulatorCondition !== null);
  }
}
